import type { ILocation } from '@model/structureData';

import type { IFlattenedItem, IUseUpdateTreeData } from '../model/location';

const getMaxDepth = (previousItem: IFlattenedItem) => {
  if (previousItem) {
    return previousItem.depth + 1;
  }

  return 0;
};

const getMinDepth = (nextItem: IFlattenedItem) => {
  if (nextItem) {
    return nextItem.depth;
  }

  return 0;
};

export const getNewDepth = (items: IFlattenedItem[], calculatedDepth: number, indexDelta: number) => {
  const previousItem = items[indexDelta - 1];
  const nextItem = items[indexDelta];

  const maxDepth = getMaxDepth(previousItem);
  const minDepth = getMinDepth(nextItem);

  let newDepth = calculatedDepth;

  if (calculatedDepth >= maxDepth) {
    newDepth = maxDepth;
  } else if (calculatedDepth < minDepth) {
    newDepth = minDepth;
  }

  return newDepth;
};

const findNewParentId = (items: IFlattenedItem[], newIndex: number, newDepth: number) => {
  for (let i = newIndex - 1; i >= 0; i--) {
    if (items[i].depth === newDepth - 1) {
      return items[i].id;
    }
  }
  return null;
};

export const getParentId = (
  newDepth: number,
  previousItem: IFlattenedItem,
  items: IFlattenedItem[],
  newIndex: number,
) => {
  if (newDepth === 0 || !previousItem) {
    return null;
  }

  if (newDepth === previousItem.depth) {
    return previousItem.parentId;
  }

  if (newDepth > previousItem.depth) {
    return previousItem.id;
  }

  return findNewParentId(items, newIndex, newDepth);
};

export const flattenTree = (
  items: ILocation[],
  visibleItems: { [id: number]: boolean },
  parentId: number | null = null,
  depth = 0,
): IFlattenedItem[] => {
  return items.flatMap((item): IFlattenedItem[] => [
    {
      id: item.id,
      name: item.name,
      tree: item.tree,
      facilityId: item.facilityId,
      levelName: item.levelName,
      parentId,
      depth,
      children: !!item.children.length,
      hidden: visibleItems[parentId!] ? false : depth !== 0,
    },
    ...flattenTree(item.children, visibleItems, item.id, depth + 1),
  ]);
};

export const getChildrenCount = (items: IFlattenedItem[], startIndex: number, depth: number) => {
  let childrenCount = 0;

  for (let i = startIndex + 1; i < items.length; i++) {
    if (items[i].depth <= depth) {
      break;
    }
    childrenCount++;
  }
  return childrenCount;
};

export const findItemDeep = (items: ILocation[], itemId: number): ILocation | null => {
  for (const item of items) {
    const { id, children } = item;

    if (id === itemId) {
      return item;
    }

    if (children.length) {
      const child = findItemDeep(children, itemId);

      if (child) {
        return child;
      }
    }
  }

  return null;
};

export const getUpdateTreeData = (
  items: IFlattenedItem[],
  activeItem: IFlattenedItem,
  parentId: number | null,
): IUseUpdateTreeData => {
  if (activeItem.levelName === 'facility') {
    const facilities = items.filter((i) => i.levelName === 'facility');
    const currentIndex = facilities.findIndex((i) => i.id === activeItem.id);
    const left = facilities[currentIndex - 1];
    const right = facilities[currentIndex + 1];
    return {
      id: activeItem.id,
      parentId: activeItem.tree,
      leftId: left?.id || undefined,
      rightId: right?.id || undefined,
    };
  }

  const sameParentLocations = items.filter((i) => i.parentId === parentId);
  const currentIndex = sameParentLocations.findIndex((i) => i.id === activeItem.id);
  const left = sameParentLocations[currentIndex - 1];
  const right = sameParentLocations[currentIndex + 1];
  return {
    id: activeItem.id,
    parentId: parentId!,
    leftId: left?.id || undefined,
    rightId: right?.id || undefined,
  };
};
